/* eslint-disable @typescript-eslint/naming-convention */

import { AnsibleBoolean } from '../interfaces/ansible';
import {
  MixpanelConfig,
  SegmentConfig,
  TrackerConfig
} from '../../tracking/interfaces/tracker-config';

declare const __HEVO_1_BASE_URL__: string;
declare const __WEBSITE_URL__: string;

declare const __DOCS_CONFIG__: {
  enabled: AnsibleBoolean;
  enabledForHevo: AnsibleBoolean;
  baseURL: string;
  miniDocsBaseURL: string;
  docsURL: string;
  miniDocsURL: string;
};

declare const __FULLSTORY_CONFIG__: TrackerConfig;
declare const __MIXPANEL_CONFIG__: MixpanelConfig;
declare const __SEGMENT_CONFIG__: SegmentConfig;
declare const __CORALOGIX_CONFIG__: {
  enabled: AnsibleBoolean;
  public_key: string;
  application: string;
  coralogixDomain: string;
};

declare const __H20_ENABLED_FEATURES__: {
  EDIT_SOURCE_DEST_NAME: AnsibleBoolean;
  EDIT_PIPELINE_CONFIG: AnsibleBoolean;
  ALLOW_COLUMN_LEVEL_CHANGES_ONLY: AnsibleBoolean;
};

declare const __DEPLOYMENT_ENV__: string;
declare const __INTERCOM_APP_ID__: string;

declare const __BASIC_AUTH_TEST__: string;

declare const __METICULOUS_RECORDER_CONFIG__: { enabled: AnsibleBoolean; projectId?: string };

declare const __USE_DEFAULT_FEATURED_CUSTOMERS__: AnsibleBoolean;

declare const __RECAPTCHA_V3_KEY__: string;
declare const __RECAPTCHA_V2_INVISIBLE_KEY__: string;

declare const __IS_H20_LIBRARY_MODE__: AnsibleBoolean;

declare const analyticsEnabled: (config: TrackerConfig) => boolean;

export function getHevo1BaseURL() {
  return typeof __HEVO_1_BASE_URL__ === 'undefined' ? 'https://beta.hevo.me' : __HEVO_1_BASE_URL__;
}

export function getWebsiteURL() {
  return typeof __WEBSITE_URL__ !== 'undefined' ? __WEBSITE_URL__ : '';
}

export function getDocsConfig() {
  return {
    ...__DOCS_CONFIG__,
    miniDocsURL: `${__DOCS_CONFIG__.miniDocsBaseURL}/edge`,
    docsURL: `${__DOCS_CONFIG__.baseURL}/edge`
  };
}

export function getMixpanelConfig() {
  return __MIXPANEL_CONFIG__;
}

export function getSegmentConfig() {
  return __SEGMENT_CONFIG__;
}

export function getFullstoryConfig() {
  return __FULLSTORY_CONFIG__;
}

export function getCoralogixConfig() {
  return __CORALOGIX_CONFIG__;
}

export function isCoralogixEnabled() {
  return __CORALOGIX_CONFIG__?.enabled === AnsibleBoolean.TRUE;
}

export function isAnalyticsEnabled(config: TrackerConfig) {
  return analyticsEnabled(config);
}

export function isCustomEventsTrackingEnabled(config: TrackerConfig) {
  return analyticsEnabled(config) && config.trackCustomEvents !== AnsibleBoolean.FALSE;
}

export function getDeploymentEnv() {
  return __DEPLOYMENT_ENV__;
}

export function getIntercomAppId() {
  return __INTERCOM_APP_ID__;
}

export function getTemporaryBasicAuthLocal() {
  if (typeof __BASIC_AUTH_TEST__ === 'undefined' || !__BASIC_AUTH_TEST__) {
    return undefined;
  }

  return __BASIC_AUTH_TEST__;
}

export function getMeticulousRecorderConfig() {
  if (typeof __METICULOUS_RECORDER_CONFIG__ === 'undefined' || !__METICULOUS_RECORDER_CONFIG__) {
    return {
      enabled: AnsibleBoolean.FALSE
    };
  }

  return __METICULOUS_RECORDER_CONFIG__;
}

export function shouldUseDefaultFeaturedCustomers() {
  return (
    typeof __USE_DEFAULT_FEATURED_CUSTOMERS__ !== 'undefined' &&
    __USE_DEFAULT_FEATURED_CUSTOMERS__ !== AnsibleBoolean.FALSE
  );
}

export function getRecaptchaV3Key() {
  return typeof __RECAPTCHA_V3_KEY__ !== 'undefined' ? __RECAPTCHA_V3_KEY__ : '';
}

export function getRecaptchaV2InvisibleKey() {
  return typeof __RECAPTCHA_V2_INVISIBLE_KEY__ !== 'undefined'
    ? __RECAPTCHA_V2_INVISIBLE_KEY__
    : '';
}

export function isLibraryMode() {
  return typeof __IS_H20_LIBRARY_MODE__ !== 'undefined' && __IS_H20_LIBRARY_MODE__ === 'True';
}

export function getHermesAPIPrefix() {
  return isLibraryMode() ? '/h20' : '';
}

export function getH20EnabledFeatures() {
  if (typeof __H20_ENABLED_FEATURES__ === 'undefined') {
    return {} as typeof __H20_ENABLED_FEATURES__;
  }

  return __H20_ENABLED_FEATURES__;
}

export function isH20FeatureEnabled(feature: keyof typeof __H20_ENABLED_FEATURES__) {
  return getH20EnabledFeatures()[feature] === AnsibleBoolean.TRUE;
}

export function isEditSourceDestNameAllowed() {
  return isH20FeatureEnabled('EDIT_SOURCE_DEST_NAME');
}

export function isEditPipelineConfigAllowed() {
  return isH20FeatureEnabled('EDIT_PIPELINE_CONFIG');
}

export function isAllowColumnLevelChangesOnlyEnabled() {
  return isH20FeatureEnabled('ALLOW_COLUMN_LEVEL_CHANGES_ONLY');
}
